/* [BD feat] Cart drawer — clear delivery attributes, checkout disabled until all items have delivery date */

function bdClearDeliveryAttributes() {
  if (typeof window.routes === 'undefined' || !window.routes.cart_update_url) return;
  const cartUrl = typeof window.routes?.cart_url !== 'undefined' ? window.routes.cart_url : '/cart';
  const baseUpdate = window.routes.cart_update_url || '/cart/update';
  const updateUrl = String(baseUpdate).endsWith('.js') ? baseUpdate : baseUpdate + '.js';
  fetch(`${cartUrl}.js`)
    .then((r) => r.json())
    .then((cart) => {
      const attrs = (cart && cart.attributes) ? cart.attributes : {};
      const deliveryKeys = Object.keys(attrs).filter(k => k === '[BD] Delivery Date' || k.startsWith('_Delivery Day ') || k.startsWith('_Delivery Date '));
      if (deliveryKeys.length === 0) return;
      const attributes = Object.fromEntries(deliveryKeys.map(k => [k, null]));
      const body = JSON.stringify({ attributes });
      const config = typeof fetchConfig === 'function' ? fetchConfig('json') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      fetch(updateUrl, { ...config, body }).then(() => {}).catch(() => {});
    })
    .catch(() => {});
}

function bdRefreshCartDrawerCheckout() {
  if (typeof window.bdUpdateCartDrawerCheckout === 'function') {
    window.bdUpdateCartDrawerCheckout();
  }
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('bd-delivery-change', bdRefreshCartDrawerCheckout);
  }

  connectedCallback() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.bdBound) {
      overlay.dataset.bdBound = 'true';
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
    if (this.classList.contains('is-empty') && typeof bdClearDeliveryAttributes === 'function') {
      bdClearDeliveryAttributes();
      if (typeof window.rrDeliveryClear === 'function') window.rrDeliveryClear(); /* [RR feat] Liefertermin leeren */
    }

    this.bdRebindCartFormGuard();
    bdRefreshCartDrawerCheckout();

    this.cartUpdateUnsubscriber = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
      ? subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.bdRebindCartFormGuard();
          requestAnimationFrame(() => bdRefreshCartDrawerCheckout());
          if (event?.cartData?.item_count === 0 && typeof bdClearDeliveryAttributes === 'function') {
            bdClearDeliveryAttributes();
            if (typeof window.rrDeliveryClear === 'function') window.rrDeliveryClear(); /* [RR feat] */
          }
        })
      : undefined;
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  bdRebindCartFormGuard() {
    const cartForm = this.querySelector('#CartDrawer-Form');
    if (!cartForm || cartForm.dataset.bdDeliveryGuard === 'true') return;

    cartForm.dataset.bdDeliveryGuard = 'true';
    cartForm.addEventListener('submit', (event) => {
      const ready = typeof window.bdAllCartItemsHaveDelivery === 'function'
        ? window.bdAllCartItemsHaveDelivery()
        : false;
      if (!ready) {
        event.preventDefault();
        bdRefreshCartDrawerCheckout();
      }
    });
  }

  bdScheduleCheckoutStateUpdate() {
    bdRefreshCartDrawerCheckout();
    requestAnimationFrame(() => bdRefreshCartDrawerCheckout());
  }

  bdAllItemsHaveDelivery() {
    return typeof window.bdAllCartItemsHaveDelivery === 'function'
      ? window.bdAllCartItemsHaveDelivery()
      : false;
  }

  bdUpdateCheckoutButtonState() {
    bdRefreshCartDrawerCheckout();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (typeof bdClearDeliveryAttributes === 'function') bdClearDeliveryAttributes();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.bdScheduleCheckoutStateUpdate();
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.bdScheduleCheckoutStateUpdate();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    if (typeof bdCloseDeliveryCalendars === 'function') bdCloseDeliveryCalendars();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && !overlay.dataset.bdBound) {
        overlay.dataset.bdBound = 'true';
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.bdRebindCartFormGuard();
      this.open();
      this.bdScheduleCheckoutStateUpdate();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    /* [BD feat] Safe parse — cart-icon-bubble section HTML always resolves even if wrapper class differs */
    if (!html || typeof html !== 'string') return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const bySelector = selector ? doc.querySelector(selector) : null;
    if (bySelector) return bySelector.innerHTML;
    const sectionEl = doc.querySelector('.shopify-section');
    if (sectionEl) return sectionEl.innerHTML;
    const first = doc.body?.firstElementChild;
    return first ? first.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (typeof CartItems !== 'undefined') {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
